import { AIModule } from '../types';

export const MULTI_AGENT_RL_MODULES: AIModule[] = [
  {
    id: 'multi-agent-intro',
    title: 'Introduction to Multi-Agent Reinforcement Learning',
    description: 'Understanding multi-agent systems, game theory foundations, and MARL challenges.',
    duration: '2.5 hours',
    difficulty: 'intermediate',
    content: `
# Introduction to Multi-Agent Reinforcement Learning

## What is Multi-Agent RL?

Multi-Agent Reinforcement Learning (MARL) extends RL to environments with multiple learning agents. Each agent learns simultaneously while adapting to others' changing behaviors.

## Key Characteristics

- **Multiple learning agents**: Several agents learning concurrently
- **Non-stationary environment**: Environment changes as other agents learn
- **Partial observability**: Agents may not observe other agents' actions/states
- **Communication**: Agents may or may not communicate
- **Coordination vs Competition**: Agents may have aligned or conflicting goals

## Game Theory Foundations

### Normal Form Games
Represented by payoff matrices showing outcomes for action combinations.

### Nash Equilibrium
A strategy profile where no agent can unilaterally improve their payoff.

### Pareto Efficiency
Outcomes where improving one agent's payoff requires reducing another's.

## MARL Challenges

### 1. Non-Stationarity
- Environment changes as other agents learn
- Violates Markov property assumptions
- Convergence guarantees no longer hold

### 2. Credit Assignment
- Difficult to determine individual agent contributions
- Reward may depend on joint actions

### 3. Scalability
- State/action space grows exponentially with agents
- Communication complexity increases

### 4. Equilibrium Selection
- Multiple equilibria may exist
- How to coordinate on desired equilibrium?

## Common MARL Settings

### 1. Cooperative MARL
- Agents share common reward
- Goal: maximize team performance
- Examples: Robot coordination, distributed control

### 2. Competitive MARL  
- Zero-sum games
- One agent's gain is another's loss
- Examples: Game playing, adversarial scenarios

### 3. Mixed-Motive MARL
- Agents have partially aligned interests
- Both cooperation and competition
- Examples: Trading, resource allocation

## Applications

- **Autonomous vehicles**: Traffic coordination
- **Resource allocation**: Cloud computing, networks
- **Gaming**: NPCs, multi-player games
- **Finance**: Algorithmic trading
- **Robotics**: Multi-robot systems
`,
    objectives: [
      'Understand multi-agent system fundamentals',
      'Apply game theory concepts to MARL',
      'Identify different types of multi-agent interactions',
      'Recognize challenges unique to MARL'
    ],
    codeExamples: [
      {
        id: 'simple-marl-env',
        title: 'Simple Multi-Agent Environment',
        description: 'Basic grid world with multiple agents',
        code: `
import numpy as np
import random
from typing import List, Tuple, Dict

class MultiAgentGridWorld:
    def __init__(self, width=5, height=5, num_agents=2):
        self.width = width
        self.height = height
        self.num_agents = num_agents
        self.agents = {}
        self.goals = {}
        self.reset()
    
    def reset(self):
        """Reset environment and place agents randomly"""
        self.agents = {}
        self.goals = {}
        
        # Place agents at random positions
        positions = set()
        for i in range(self.num_agents):
            while True:
                pos = (random.randint(0, self.height-1), random.randint(0, self.width-1))
                if pos not in positions:
                    positions.add(pos)
                    self.agents[i] = pos
                    break
        
        # Place goals at random positions (different from agents)
        for i in range(self.num_agents):
            while True:
                pos = (random.randint(0, self.height-1), random.randint(0, self.width-1))
                if pos not in positions:
                    positions.add(pos)
                    self.goals[i] = pos
                    break
        
        return self.get_observations()
    
    def get_observations(self):
        """Get observations for all agents"""
        observations = {}
        for agent_id in self.agents:
            # Each agent observes its position and goal
            obs = {
                'position': self.agents[agent_id],
                'goal': self.goals[agent_id],
                'other_agents': {aid: pos for aid, pos in self.agents.items() if aid != agent_id}
            }
            observations[agent_id] = obs
        return observations
    
    def step(self, actions: Dict[int, int]):
        """Execute actions for all agents simultaneously"""
        new_positions = {}
        
        # Calculate new positions
        for agent_id, action in actions.items():
            x, y = self.agents[agent_id]
            
            # Actions: 0=up, 1=right, 2=down, 3=left, 4=stay
            if action == 0 and x > 0:  # up
                new_pos = (x-1, y)
            elif action == 1 and y < self.width-1:  # right
                new_pos = (x, y+1)
            elif action == 2 and x < self.height-1:  # down
                new_pos = (x+1, y)
            elif action == 3 and y > 0:  # left
                new_pos = (x, y-1)
            else:  # stay or invalid move
                new_pos = (x, y)
            
            new_positions[agent_id] = new_pos
        
        # Handle collisions - agents can't occupy same cell
        final_positions = {}
        for agent_id, new_pos in new_positions.items():
            # Check if position is occupied by another agent
            occupied = any(pos == new_pos for aid, pos in final_positions.items())
            
            if occupied:
                # Stay in current position if collision
                final_positions[agent_id] = self.agents[agent_id]
            else:
                final_positions[agent_id] = new_pos
        
        # Update agent positions
        self.agents = final_positions
        
        # Calculate rewards
        rewards = {}
        done = {}
        
        for agent_id in self.agents:
            # Reward for reaching goal
            if self.agents[agent_id] == self.goals[agent_id]:
                rewards[agent_id] = 10
                done[agent_id] = True
            else:
                # Small penalty for each step
                rewards[agent_id] = -0.1
                done[agent_id] = False
        
        # Check if all agents reached their goals
        all_done = all(done.values())
        
        return self.get_observations(), rewards, done, all_done
    
    def render(self):
        """Simple text rendering of the environment"""
        grid = [['.' for _ in range(self.width)] for _ in range(self.height)]
        
        # Place goals
        for agent_id, (x, y) in self.goals.items():
            grid[x][y] = f'G{agent_id}'
        
        # Place agents (overwrite goals if same position)
        for agent_id, (x, y) in self.agents.items():
            grid[x][y] = f'A{agent_id}'
        
        for row in grid:
            print(' '.join(f'{cell:3}' for cell in row))
        print()

class RandomAgent:
    def __init__(self, agent_id):
        self.agent_id = agent_id
    
    def get_action(self, observation):
        """Random action selection"""
        return random.randint(0, 4)  # 5 actions: 4 directions + stay

class GreedyAgent:
    def __init__(self, agent_id):
        self.agent_id = agent_id
    
    def get_action(self, observation):
        """Move towards goal greedily"""
        agent_pos = observation['position']
        goal_pos = observation['goal']
        
        dx = goal_pos[0] - agent_pos[0]
        dy = goal_pos[1] - agent_pos[1]
        
        # Choose action based on largest distance
        if abs(dx) > abs(dy):
            if dx > 0:
                return 2  # down
            elif dx < 0:
                return 0  # up
        else:
            if dy > 0:
                return 1  # right
            elif dy < 0:
                return 3  # left
        
        return 4  # stay if at goal

# Example usage
env = MultiAgentGridWorld(width=5, height=5, num_agents=2)
agents = [RandomAgent(0), GreedyAgent(1)]

# Run episode
observations = env.reset()
print("Initial state:")
env.render()

for step in range(20):
    # Get actions from all agents
    actions = {}
    for i, agent in enumerate(agents):
        actions[i] = agent.get_action(observations[i])
    
    # Step environment
    observations, rewards, done, all_done = env.step(actions)
    
    print(f"Step {step + 1}:")
    print(f"Actions: {actions}")
    print(f"Rewards: {rewards}")
    env.render()
    
    if all_done:
        print("All agents reached their goals!")
        break
`,
        language: 'python',
        category: 'demo'
      }
    ],
    quiz: {
      questions: [
        {
          id: 'marl-challenge',
          question: 'What is the primary challenge that distinguishes MARL from single-agent RL?',
          options: [
            'Larger state spaces',
            'Non-stationary environment due to other learning agents',
            'More complex reward functions',
            'Higher computational requirements'
          ],
          correctAnswer: 1,
          explanation: 'The non-stationary environment caused by other learning agents is the fundamental challenge in MARL, as it violates the Markov property assumptions.'
        }
      ]
    }
  },
  {
    id: 'independent-learning',
    title: 'Independent Learning Approaches',
    description: 'Independent Q-learning, policy gradient methods, and their convergence properties.',
    duration: '3 hours',
    difficulty: 'intermediate',
    content: `
# Independent Learning in Multi-Agent RL

## Overview

Independent Learning treats other agents as part of the environment. Each agent learns independently using single-agent RL algorithms, ignoring the fact that other agents are also learning.

## Independent Q-Learning (IQL)

Each agent maintains its own Q-table and updates it using standard Q-learning:

Q_i(s, a_i) ← Q_i(s, a_i) + α[r_i + γ max_{a'_i} Q_i(s', a'_i) - Q_i(s, a_i)]

Where:
- i denotes agent index
- s is the joint state (may be partially observable)
- a_i is agent i's action
- r_i is agent i's reward

## Advantages of Independent Learning

1. **Simplicity**: Use existing single-agent algorithms
2. **Scalability**: Computational complexity scales linearly
3. **Decentralized**: No communication required
4. **Privacy**: Agents don't need to share information

## Limitations

1. **No convergence guarantees**: Environment is non-stationary
2. **Poor coordination**: Agents can't coordinate actions
3. **Suboptimal solutions**: May converge to poor equilibria
4. **Learning interference**: Agents may interfere with each other's learning

## When Independent Learning Works

- **Sparse interactions**: Agents rarely interact
- **Weakly coupled systems**: Agent actions have minimal impact on others
- **Large systems**: Communication overhead would be prohibitive
- **Exploratory phase**: Before implementing coordination mechanisms

## Practical Considerations

### Experience Replay in MARL
Standard experience replay may be problematic:
- Stored experiences become stale as other agents learn
- Need to consider recency of experiences
- May need agent-specific replay buffers

### Exploration in MARL
- Independent exploration may be inefficient
- Need to balance exploration vs exploitation across agents
- Consider coordinated exploration strategies

## Empirical Analysis

Research shows Independent Learning:
- Often converges in practice despite lack of theoretical guarantees
- Performance degrades with increasing number of agents
- Works better in cooperative vs competitive settings
- Benefits from careful hyperparameter tuning
`,
    objectives: [
      'Implement Independent Q-Learning for multiple agents',
      'Understand convergence limitations in MARL',
      'Analyze when independent learning is appropriate',
      'Compare performance across different multi-agent scenarios'
    ],
    codeExamples: [
      {
        id: 'independent-q-learning',
        title: 'Independent Q-Learning Implementation',
        description: 'Multiple agents learning independently using Q-learning',
        code: `
import numpy as np
import random
from collections import defaultdict
import matplotlib.pyplot as plt

class IndependentQLearningAgent:
    def __init__(self, agent_id, num_actions, learning_rate=0.1, epsilon=0.1, gamma=0.9):
        self.agent_id = agent_id
        self.num_actions = num_actions
        self.alpha = learning_rate
        self.epsilon = epsilon
        self.gamma = gamma
        self.q_table = defaultdict(lambda: defaultdict(float))
        self.episode_rewards = []
    
    def get_state_key(self, observation):
        """Convert observation to state key for Q-table"""
        # Use agent's position and goal as state
        pos = observation['position']
        goal = observation['goal']
        return f"{pos[0]},{pos[1]},{goal[0]},{goal[1]}"
    
    def get_action(self, observation, training=True):
        """ε-greedy action selection"""
        state_key = self.get_state_key(observation)
        
        if training and random.random() < self.epsilon:
            return random.randint(0, self.num_actions - 1)
        else:
            # Greedy action
            q_values = [self.q_table[state_key][a] for a in range(self.num_actions)]
            return np.argmax(q_values)
    
    def update(self, observation, action, reward, next_observation, done):
        """Q-learning update"""
        state_key = self.get_state_key(observation)
        next_state_key = self.get_state_key(next_observation)
        
        # Current Q-value
        current_q = self.q_table[state_key][action]
        
        # Next state max Q-value
        if done:
            next_max_q = 0
        else:
            next_q_values = [self.q_table[next_state_key][a] for a in range(self.num_actions)]
            next_max_q = max(next_q_values)
        
        # Q-learning update
        target = reward + self.gamma * next_max_q
        self.q_table[state_key][action] = current_q + self.alpha * (target - current_q)
    
    def decay_epsilon(self, decay_rate=0.995):
        """Decay exploration rate"""
        self.epsilon = max(0.01, self.epsilon * decay_rate)

class MultiAgentTrainer:
    def __init__(self, env, agents):
        self.env = env
        self.agents = agents
        self.training_stats = {
            'episode_rewards': {agent.agent_id: [] for agent in agents},
            'episode_lengths': [],
            'success_rate': []
        }
    
    def train_episode(self):
        """Train agents for one episode"""
        observations = self.env.reset()
        episode_rewards = {agent.agent_id: 0 for agent in self.agents}
        step_count = 0
        max_steps = 100
        
        # Store initial observations for each agent
        prev_observations = observations.copy()
        
        while step_count < max_steps:
            # Get actions from all agents
            actions = {}
            for agent in self.agents:
                actions[agent.agent_id] = agent.get_action(observations[agent.agent_id])
            
            # Step environment
            next_observations, rewards, done, all_done = self.env.step(actions)
            
            # Update each agent
            for agent in self.agents:
                agent_id = agent.agent_id
                agent.update(
                    prev_observations[agent_id],
                    actions[agent_id],
                    rewards[agent_id],
                    next_observations[agent_id],
                    done[agent_id]
                )
                episode_rewards[agent_id] += rewards[agent_id]
            
            prev_observations = observations
            observations = next_observations
            step_count += 1
            
            if all_done:
                break
        
        # Record statistics
        for agent in self.agents:
            self.training_stats['episode_rewards'][agent.agent_id].append(
                episode_rewards[agent.agent_id]
            )
        
        self.training_stats['episode_lengths'].append(step_count)
        self.training_stats['success_rate'].append(1.0 if all_done else 0.0)
        
        return episode_rewards, step_count, all_done
    
    def train(self, num_episodes):
        """Train agents for multiple episodes"""
        for episode in range(num_episodes):
            episode_rewards, steps, success = self.train_episode()
            
            # Decay epsilon for all agents
            for agent in self.agents:
                agent.decay_epsilon()
            
            # Print progress
            if episode % 100 == 0:
                recent_success = np.mean(self.training_stats['success_rate'][-100:])
                avg_steps = np.mean(self.training_stats['episode_lengths'][-100:])
                print(f"Episode {episode}: Success Rate: {recent_success:.2f}, Avg Steps: {avg_steps:.1f}")
                
                for agent in self.agents:
                    avg_reward = np.mean(self.training_stats['episode_rewards'][agent.agent_id][-100:])
                    print(f"  Agent {agent.agent_id}: Avg Reward: {avg_reward:.2f}, ε: {agent.epsilon:.3f}")
        
        return self.training_stats

# Example: Training multiple agents independently
from multi_agent_gridworld import MultiAgentGridWorld  # From previous example

# Create environment and agents
env = MultiAgentGridWorld(width=6, height=6, num_agents=2)
agents = [
    IndependentQLearningAgent(agent_id=0, num_actions=5, learning_rate=0.1, epsilon=0.3),
    IndependentQLearningAgent(agent_id=1, num_actions=5, learning_rate=0.1, epsilon=0.3)
]

# Train agents
trainer = MultiAgentTrainer(env, agents)
print("Training independent Q-learning agents...")
stats = trainer.train(num_episodes=2000)

# Analyze results
print("\\nTraining completed!")
print(f"Final success rate: {np.mean(stats['success_rate'][-100:]):.2f}")
print(f"Final average steps: {np.mean(stats['episode_lengths'][-100:]):.1f}")

# Test trained agents
print("\\nTesting trained agents:")
test_episodes = 10
test_success = 0

for test_ep in range(test_episodes):
    observations = env.reset()
    steps = 0
    
    while steps < 50:
        actions = {}
        for agent in agents:
            # Use trained policy (no exploration)
            actions[agent.agent_id] = agent.get_action(observations[agent.agent_id], training=False)
        
        observations, rewards, done, all_done = env.step(actions)
        steps += 1
        
        if all_done:
            test_success += 1
            break
    
    print(f"Test episode {test_ep + 1}: {'Success' if all_done else 'Failed'} in {steps} steps")

print(f"\\nTest success rate: {test_success/test_episodes:.2f}")
`,
        language: 'python',
        category: 'tutorial'
      }
    ],
    practice: {
      task: 'Implement Independent Policy Gradient agents and compare their performance with Independent Q-Learning in the multi-agent grid world.',
      hints: [
        'Use REINFORCE algorithm for each agent independently',
        'Store episode trajectories for policy updates',
        'Compare convergence speed and final performance'
      ]
    }
  },
  {
    id: 'cooperative-marl',
    title: 'Cooperative Multi-Agent Methods',
    description: 'Centralized training with decentralized execution, value decomposition, and communication.',
    duration: '3.5 hours',
    difficulty: 'advanced',
    content: `
# Cooperative Multi-Agent Reinforcement Learning

## Overview

Cooperative MARL focuses on scenarios where agents share a common goal or have aligned interests. The challenge is coordinating actions to maximize team performance.

## Key Paradigms

### 1. Centralized Training, Decentralized Execution (CTDE)
- **Training**: Access to global information and centralized critic
- **Execution**: Agents act independently using local observations
- **Benefits**: Addresses non-stationarity during training while maintaining decentralized execution

### 2. Value Decomposition Methods
Break down team value function into individual agent contributions:

Q_total(s, a_1, ..., a_n) = f(Q_1(s, a_1), ..., Q_n(s, a_n))

Popular approaches:
- **VDN (Value Decomposition Networks)**: Additive decomposition
- **QMIX**: Monotonic decomposition using mixing networks

### 3. Communication-Based Methods
Agents learn when and what to communicate:
- **Differentiable communication**: Learn communication through gradients
- **Discrete communication**: Use attention mechanisms
- **Bandwidth constraints**: Limited communication capacity

## Value Decomposition Networks (VDN)

Assumes team Q-value is sum of individual Q-values:

Q_total(s, a) = Σ_i Q_i(s_i, a_i)

**Advantages**:
- Simple and interpretable
- Easy to implement
- Decentralized execution

**Limitations**:
- Restrictive additivity assumption
- Cannot represent complex coordination

## QMIX Algorithm

Uses mixing network to combine agent Q-values:

Q_total(s, a) = f_mix(Q_1, ..., Q_n; s)

**Key Innovation**: Monotonicity constraint ensures:
∂Q_total/∂Q_i ≥ 0

This guarantees that improving individual Q-values improves team performance.

## Multi-Agent Actor-Critic (MAAC)

Extends actor-critic to multi-agent setting:
- **Centralized Critic**: Observes global state and all actions
- **Decentralized Actors**: Use local observations
- **Policy Gradient**: Update actors using centralized value function

## Communication Strategies

### 1. Learned Communication
Agents learn communication protocol through backpropagation:
- What to communicate (message content)
- When to communicate (timing)
- To whom to communicate (addressing)

### 2. Attention-Based Communication
Use attention mechanisms to focus on relevant information:
- Agents attend to other agents selectively
- Learn importance weights for different information sources

### 3. Emergent Communication
Communication protocols emerge from optimization:
- No pre-defined communication structure
- Agents develop their own "language"
- Often requires regularization to prevent overfitting

## Challenges in Cooperative MARL

1. **Credit Assignment**: Which agent deserves credit for team success?
2. **Scalability**: How to handle large numbers of agents?
3. **Partial Observability**: Agents have limited local views
4. **Communication Constraints**: Bandwidth and latency limits
5. **Robustness**: Performance when some agents fail or behave differently
`,
    objectives: [
      'Understand CTDE paradigm and its benefits',
      'Implement value decomposition methods',
      'Design communication protocols for coordination',
      'Analyze trade-offs between different cooperative approaches'
    ],
    codeExamples: [
      {
        id: 'vdn-implementation',
        title: 'Value Decomposition Networks (VDN)',
        description: 'Implementation of VDN for cooperative multi-agent learning',
        code: `
import torch
import torch.nn as nn
import torch.optim as optim
import torch.nn.functional as F
import numpy as np
from collections import deque
import random

class DQNAgent(nn.Module):
    def __init__(self, input_size, hidden_size, output_size):
        super(DQNAgent, self).__init__()
        self.network = nn.Sequential(
            nn.Linear(input_size, hidden_size),
            nn.ReLU(),
            nn.Linear(hidden_size, hidden_size),
            nn.ReLU(),
            nn.Linear(hidden_size, output_size)
        )
    
    def forward(self, x):
        return self.network(x)

class VDNMixer(nn.Module):
    def __init__(self, num_agents):
        super(VDNMixer, self).__init__()
        self.num_agents = num_agents
    
    def forward(self, agent_q_values):
        """Simple additive mixing for VDN"""
        return torch.sum(agent_q_values, dim=1)

class VDNTrainer:
    def __init__(self, num_agents, state_size, action_size, hidden_size=64, lr=0.001):
        self.num_agents = num_agents
        self.state_size = state_size
        self.action_size = action_size
        
        # Create individual agent networks
        self.agents = nn.ModuleList([
            DQNAgent(state_size, hidden_size, action_size) 
            for _ in range(num_agents)
        ])
        
        # Create target networks
        self.target_agents = nn.ModuleList([
            DQNAgent(state_size, hidden_size, action_size) 
            for _ in range(num_agents)
        ])
        
        # VDN mixer
        self.mixer = VDNMixer(num_agents)
        
        # Optimizers
        self.agent_optimizers = [
            optim.Adam(agent.parameters(), lr=lr) 
            for agent in self.agents
        ]
        
        # Experience replay
        self.memory = deque(maxlen=10000)
        self.batch_size = 32
        self.gamma = 0.99
        self.epsilon = 0.3
        self.epsilon_decay = 0.995
        self.epsilon_min = 0.01
        
        # Update target networks
        self.update_target_networks()
        self.target_update_freq = 100
        self.step_count = 0
    
    def update_target_networks(self):
        """Copy weights to target networks"""
        for i in range(self.num_agents):
            self.target_agents[i].load_state_dict(self.agents[i].state_dict())
    
    def get_actions(self, states, training=True):
        """Get actions for all agents using ε-greedy policy"""
        actions = []
        
        for i, state in enumerate(states):
            if training and random.random() < self.epsilon:
                action = random.randint(0, self.action_size - 1)
            else:
                state_tensor = torch.FloatTensor(state).unsqueeze(0)
                q_values = self.agents[i](state_tensor)
                action = q_values.argmax().item()
            actions.append(action)
        
        return actions
    
    def store_experience(self, states, actions, rewards, next_states, dones):
        """Store team experience in replay buffer"""
        self.memory.append((states, actions, rewards, next_states, dones))
    
    def train_step(self):
        """Perform one training step using VDN"""
        if len(self.memory) < self.batch_size:
            return
        
        # Sample batch
        batch = random.sample(self.memory, self.batch_size)
        
        # Unpack batch
        states_batch = []
        actions_batch = []
        rewards_batch = []
        next_states_batch = []
        dones_batch = []
        
        for states, actions, rewards, next_states, dones in batch:
            states_batch.append(states)
            actions_batch.append(actions)
            rewards_batch.append(sum(rewards))  # Team reward
            next_states_batch.append(next_states)
            dones_batch.append(any(dones))  # Team done
        
        # Convert to tensors
        rewards_tensor = torch.FloatTensor(rewards_batch)
        dones_tensor = torch.BoolTensor(dones_batch)
        
        # Compute current Q-values for each agent
        current_q_values = []
        for i in range(self.num_agents):
            agent_states = torch.FloatTensor([states[i] for states in states_batch])
            agent_actions = torch.LongTensor([actions[i] for actions in actions_batch])
            
            q_values = self.agents[i](agent_states)
            current_q = q_values.gather(1, agent_actions.unsqueeze(1)).squeeze()
            current_q_values.append(current_q)
        
        # Stack Q-values for mixing
        current_q_stack = torch.stack(current_q_values, dim=1)
        
        # Mix current Q-values using VDN
        current_total_q = self.mixer(current_q_stack)
        
        # Compute target Q-values
        next_q_values = []
        for i in range(self.num_agents):
            agent_next_states = torch.FloatTensor([states[i] for states in next_states_batch])
            next_q = self.target_agents[i](agent_next_states)
            max_next_q = next_q.max(1)[0]
            next_q_values.append(max_next_q)
        
        # Stack and mix target Q-values
        next_q_stack = torch.stack(next_q_values, dim=1)
        next_total_q = self.mixer(next_q_stack)
        
        # Compute targets
        targets = rewards_tensor + self.gamma * next_total_q * ~dones_tensor
        
        # Compute loss
        loss = F.mse_loss(current_total_q, targets.detach())
        
        # Update all agents
        for optimizer in self.agent_optimizers:
            optimizer.zero_grad()
        
        loss.backward()
        
        for optimizer in self.agent_optimizers:
            optimizer.step()
        
        # Update target networks periodically
        self.step_count += 1
        if self.step_count % self.target_update_freq == 0:
            self.update_target_networks()
        
        # Decay epsilon
        if self.epsilon > self.epsilon_min:
            self.epsilon *= self.epsilon_decay
        
        return loss.item()

# Cooperative environment example
class CooperativePickup:
    def __init__(self, grid_size=5, num_agents=2, num_packages=3):
        self.grid_size = grid_size
        self.num_agents = num_agents
        self.num_packages = num_packages
        self.reset()
    
    def reset(self):
        """Reset environment"""
        # Place agents
        self.agents = []
        for _ in range(self.num_agents):
            pos = (
                np.random.randint(0, self.grid_size),
                np.random.randint(0, self.grid_size)
            )
            self.agents.append({'pos': pos, 'carrying': False})
        
        # Place packages
        self.packages = []
        for _ in range(self.num_packages):
            pos = (
                np.random.randint(0, self.grid_size),
                np.random.randint(0, self.grid_size)
            )
            self.packages.append({'pos': pos, 'picked': False})
        
        # Delivery zone (center of grid)
        self.delivery_zone = (self.grid_size // 2, self.grid_size // 2)
        
        return self.get_observations()
    
    def get_observations(self):
        """Get observations for all agents"""
        observations = []
        
        for i, agent in enumerate(self.agents):
            obs = []
            
            # Agent position (normalized)
            obs.extend([agent['pos'][0] / self.grid_size, agent['pos'][1] / self.grid_size])
            
            # Whether carrying package
            obs.append(float(agent['carrying']))
            
            # Nearest unpicked package position
            unpicked_packages = [p for p in self.packages if not p['picked']]
            if unpicked_packages:
                distances = [
                    abs(agent['pos'][0] - p['pos'][0]) + abs(agent['pos'][1] - p['pos'][1])
                    for p in unpicked_packages
                ]
                nearest_idx = np.argmin(distances)
                nearest_package = unpicked_packages[nearest_idx]
                obs.extend([
                    nearest_package['pos'][0] / self.grid_size,
                    nearest_package['pos'][1] / self.grid_size
                ])
            else:
                obs.extend([0.0, 0.0])
            
            # Delivery zone position
            obs.extend([
                self.delivery_zone[0] / self.grid_size,
                self.delivery_zone[1] / self.grid_size
            ])
            
            observations.append(obs)
        
        return observations
    
    def step(self, actions):
        """Execute actions for all agents"""
        rewards = [0] * self.num_agents
        
        # Move agents
        for i, action in enumerate(actions):
            agent = self.agents[i]
            x, y = agent['pos']
            
            # Actions: 0=up, 1=right, 2=down, 3=left, 4=pickup/drop
            if action == 0 and x > 0:
                agent['pos'] = (x - 1, y)
            elif action == 1 and y < self.grid_size - 1:
                agent['pos'] = (x, y + 1)
            elif action == 2 and x < self.grid_size - 1:
                agent['pos'] = (x + 1, y)
            elif action == 3 and y > 0:
                agent['pos'] = (x, y - 1)
            elif action == 4:
                # Pickup or drop
                if not agent['carrying']:
                    # Try to pickup package at current position
                    for package in self.packages:
                        if package['pos'] == agent['pos'] and not package['picked']:
                            package['picked'] = True
                            agent['carrying'] = True
                            rewards[i] += 5  # Pickup reward
                            break
                else:
                    # Try to drop at delivery zone
                    if agent['pos'] == self.delivery_zone:
                        agent['carrying'] = False
                        rewards[i] += 10  # Delivery reward
        
        # Check if all packages delivered
        all_delivered = all(p['picked'] for p in self.packages) and all(not a['carrying'] for a in self.agents)
        
        # Team bonus for completing task
        if all_delivered:
            for i in range(self.num_agents):
                rewards[i] += 20
        
        # Small step penalty to encourage efficiency
        for i in range(self.num_agents):
            rewards[i] -= 0.1
        
        dones = [all_delivered] * self.num_agents
        
        return self.get_observations(), rewards, dones

# Training example
env = CooperativePickup(grid_size=5, num_agents=2, num_packages=2)
trainer = VDNTrainer(
    num_agents=2,
    state_size=7,  # Observation size
    action_size=5,  # Number of actions
    lr=0.001
)

# Training loop
num_episodes = 1000
episode_rewards = []

for episode in range(num_episodes):
    states = env.reset()
    total_reward = 0
    steps = 0
    max_steps = 50
    
    while steps < max_steps:
        actions = trainer.get_actions(states)
        next_states, rewards, dones = env.step(actions)
        
        trainer.store_experience(states, actions, rewards, next_states, dones)
        
        if len(trainer.memory) > trainer.batch_size:
            loss = trainer.train_step()
        
        total_reward += sum(rewards)
        states = next_states
        steps += 1
        
        if any(dones):
            break
    
    episode_rewards.append(total_reward)
    
    if episode % 100 == 0:
        avg_reward = np.mean(episode_rewards[-100:])
        print(f"Episode {episode}: Avg Reward: {avg_reward:.2f}, ε: {trainer.epsilon:.3f}")

print("VDN training completed!")
`,
        language: 'python',
        category: 'tutorial'
      }
    ],
    practice: {
      task: 'Implement QMIX algorithm by replacing the VDN mixer with a monotonic mixing network.',
      hints: [
        'Use a hypernetwork to generate mixing network weights',
        'Ensure monotonicity constraint: ∂Q_total/∂Q_i ≥ 0',
        'Use absolute values or non-negative activations for monotonicity'
      ]
    }
  },
  {
    id: 'game-ai-applications',
    title: 'Game AI and Strategic Applications',
    description: 'Self-play, population-based training, and competitive multi-agent scenarios.',
    duration: '3.5 hours',
    difficulty: 'advanced',
    content: `
# Game AI and Strategic Applications

## Self-Play in Multi-Agent Learning

Self-play trains agents by playing against copies of themselves, enabling:
- **Curriculum learning**: Difficulty automatically adjusts as agent improves
- **Diverse strategies**: Exposure to different playing styles
- **No human data required**: Can surpass human performance
- **Exploitability**: May develop strategies that exploit specific weaknesses

### Self-Play Algorithm:
1. Initialize agent population
2. For each iteration:
   - Agents play against themselves or previous versions
   - Collect game outcomes and trajectories
   - Update agent policies using RL
   - Optionally add current agent to opponent pool

## Population-Based Training

Maintains a diverse population of agents to prevent overfitting:

### Key Benefits:
- **Robustness**: Agents face diverse opponents
- **Meta-learning**: Can adapt to different opponent types
- **Exploration**: Population diversity maintains exploration
- **Transfer**: Skills transfer across different game variants

### Population Management:
- **League systems**: Ranked competitions between agents
- **Diversity metrics**: Encourage behavioral diversity
- **Evolutionary selection**: Keep best performing agents
- **Periodic reset**: Prevent local optima

## Competitive Multi-Agent Scenarios

### Zero-Sum Games
- One agent's gain equals another's loss
- Nash equilibrium as solution concept
- Minimax algorithms for perfect information games
- Expected value calculations for stochastic games

### General-Sum Games
- Agents can have partially aligned interests
- Multiple equilibria possible
- Coordination and communication important
- Examples: Auction mechanisms, resource allocation

## Advanced Techniques

### 1. Fictitious Play
Agents assume opponents play according to historical frequencies:
- Maintain belief about opponent strategies
- Best respond to empirical distribution
- Convergence guarantees for certain game classes

### 2. Multi-Agent Deep Deterministic Policy Gradient (MADDPG)
Extension of DDPG to multi-agent settings:
- Centralized training with decentralized execution
- Actor-critic architecture for continuous actions
- Experience replay with multi-agent trajectories

### 3. Neural Fictitious Self-Play (NFSP)
Combines best response and average strategy:
- Maintain two networks: best response and average strategy
- Sample from mixture during training
- Approximates Nash equilibrium in extensive-form games

## Game-Theoretic Concepts

### Nash Equilibrium
Strategy profile where no agent can unilaterally improve:
∀i, u_i(s_i*, s_{-i}*) ≥ u_i(s_i, s_{-i}*) for all s_i

### Correlated Equilibrium
Agents follow recommendations from external correlator:
- Can achieve better outcomes than Nash equilibrium
- Relevant for mechanism design and auctions

### Stackelberg Equilibrium
Leader-follower games with sequential moves:
- Leader commits to strategy first
- Follower optimizes given leader's strategy
- First-mover advantage possible

## Applications

### 1. Trading and Auctions
- **High-frequency trading**: React to market conditions
- **Ad auctions**: Bid optimization in real-time
- **Spectrum auctions**: Government spectrum allocation

### 2. Security Games
- **Network security**: Defender vs attacker scenarios
- **Physical security**: Patrol scheduling, resource allocation
- **Cybersecurity**: Intrusion detection and response

### 3. Autonomous Systems
- **Traffic management**: Coordinated vehicle routing
- **Resource allocation**: Cloud computing, smart grids
- **Supply chain**: Multi-party logistics optimization

## Challenges and Solutions

### 1. Non-Stationarity
**Problem**: Environment changes as opponents adapt
**Solutions**: 
- Experience replay with recency weighting
- Opponent modeling and adaptation
- Robust strategy learning

### 2. Exploitability
**Problem**: Strategies may be exploitable by specific opponents
**Solutions**:
- Population-based training
- Diverse opponent sampling
- Robustness evaluation

### 3. Sample Efficiency
**Problem**: Multi-agent learning requires many interactions
**Solutions**:
- Transfer learning between game variants
- Curriculum learning with staged opponents
- Model-based planning approaches
`,
    objectives: [
      'Implement self-play training systems',
      'Understand population-based training benefits',
      'Apply game theory concepts to multi-agent scenarios',
      'Design robust agents for competitive environments'
    ],
    codeExamples: [
      {
        id: 'self-play-implementation',
        title: 'Self-Play Training System',
        description: 'Implementation of self-play with opponent pool management',
        code: `
import torch
import torch.nn as nn
import torch.optim as optim
import numpy as np
import random
from collections import deque, defaultdict
import copy

class PolicyNetwork(nn.Module):
    def __init__(self, input_size, hidden_size, output_size):
        super(PolicyNetwork, self).__init__()
        self.network = nn.Sequential(
            nn.Linear(input_size, hidden_size),
            nn.ReLU(),
            nn.Linear(hidden_size, hidden_size),
            nn.ReLU(),
            nn.Linear(hidden_size, output_size),
            nn.Softmax(dim=-1)
        )
    
    def forward(self, x):
        return self.network(x)

class ValueNetwork(nn.Module):
    def __init__(self, input_size, hidden_size):
        super(ValueNetwork, self).__init__()
        self.network = nn.Sequential(
            nn.Linear(input_size, hidden_size),
            nn.ReLU(),
            nn.Linear(hidden_size, hidden_size),
            nn.ReLU(),
            nn.Linear(hidden_size, 1)
        )
    
    def forward(self, x):
        return self.network(x)

class SelfPlayAgent:
    def __init__(self, state_size, action_size, hidden_size=64, lr=0.001):
        self.state_size = state_size
        self.action_size = action_size
        
        # Policy and value networks
        self.policy_net = PolicyNetwork(state_size, hidden_size, action_size)
        self.value_net = ValueNetwork(state_size, hidden_size)
        
        # Optimizers
        self.policy_optimizer = optim.Adam(self.policy_net.parameters(), lr=lr)
        self.value_optimizer = optim.Adam(self.value_net.parameters(), lr=lr)
        
        # Training statistics
        self.win_rate = 0.0
        self.games_played = 0
        self.version = 0
    
    def get_action(self, state, training=True):
        """Get action using current policy"""
        state_tensor = torch.FloatTensor(state).unsqueeze(0)
        
        with torch.no_grad():
            action_probs = self.policy_net(state_tensor)
        
        if training:
            # Sample from probability distribution
            action = torch.multinomial(action_probs, 1).item()
        else:
            # Greedy action selection
            action = action_probs.argmax().item()
        
        return action, action_probs[0]
    
    def get_value(self, state):
        """Get state value estimate"""
        state_tensor = torch.FloatTensor(state).unsqueeze(0)
        with torch.no_grad():
            value = self.value_net(state_tensor)
        return value.item()
    
    def update(self, states, actions, rewards, action_probs):
        """Update policy and value networks using collected trajectories"""
        states_tensor = torch.FloatTensor(states)
        actions_tensor = torch.LongTensor(actions)
        rewards_tensor = torch.FloatTensor(rewards)
        
        # Calculate returns (simple Monte Carlo)
        returns = []
        G = 0
        for r in reversed(rewards):
            G = r + 0.99 * G  # Discount factor = 0.99
            returns.insert(0, G)
        returns_tensor = torch.FloatTensor(returns)
        
        # Normalize returns
        returns_tensor = (returns_tensor - returns_tensor.mean()) / (returns_tensor.std() + 1e-8)
        
        # Value network update
        value_predictions = self.value_net(states_tensor).squeeze()
        value_loss = nn.MSELoss()(value_predictions, returns_tensor)
        
        self.value_optimizer.zero_grad()
        value_loss.backward()
        self.value_optimizer.step()
        
        # Policy network update (REINFORCE with baseline)
        advantages = returns_tensor - value_predictions.detach()
        action_log_probs = torch.log(torch.stack(action_probs))
        selected_log_probs = action_log_probs.gather(1, actions_tensor.unsqueeze(1)).squeeze()
        
        policy_loss = -(selected_log_probs * advantages).mean()
        
        self.policy_optimizer.zero_grad()
        policy_loss.backward()
        self.policy_optimizer.step()
        
        return policy_loss.item(), value_loss.item()
    
    def save_checkpoint(self, filepath):
        """Save agent checkpoint"""
        torch.save({
            'policy_state_dict': self.policy_net.state_dict(),
            'value_state_dict': self.value_net.state_dict(),
            'win_rate': self.win_rate,
            'games_played': self.games_played,
            'version': self.version
        }, filepath)
    
    def load_checkpoint(self, filepath):
        """Load agent checkpoint"""
        checkpoint = torch.load(filepath)
        self.policy_net.load_state_dict(checkpoint['policy_state_dict'])
        self.value_net.load_state_dict(checkpoint['value_state_dict'])
        self.win_rate = checkpoint['win_rate']
        self.games_played = checkpoint['games_played']
        self.version = checkpoint['version']

class OpponentPool:
    def __init__(self, max_size=10):
        self.max_size = max_size
        self.agents = []
        self.win_rates = []
        
    def add_agent(self, agent, win_rate):
        """Add agent to opponent pool"""
        # Deep copy to preserve agent state
        agent_copy = copy.deepcopy(agent)
        agent_copy.version = len(self.agents)
        
        self.agents.append(agent_copy)
        self.win_rates.append(win_rate)
        
        # Remove oldest agent if pool is full
        if len(self.agents) > self.max_size:
            self.agents.pop(0)
            self.win_rates.pop(0)
    
    def sample_opponent(self, current_agent_winrate=0.5):
        """Sample opponent based on win rates and diversity"""
        if not self.agents:
            return None
        
        # Sample more from agents with similar win rates
        weights = []
        for wr in self.win_rates:
            # Prefer opponents with similar skill level
            similarity = 1.0 / (1.0 + abs(wr - current_agent_winrate))
            weights.append(similarity)
        
        # Normalize weights
        total_weight = sum(weights)
        if total_weight > 0:
            weights = [w / total_weight for w in weights]
            idx = np.random.choice(len(self.agents), p=weights)
        else:
            idx = random.randint(0, len(self.agents) - 1)
        
        return self.agents[idx]
    
    def get_stats(self):
        """Get opponent pool statistics"""
        if not self.agents:
            return "Empty opponent pool"
        
        return f"Pool size: {len(self.agents)}, Win rates: {[f'{wr:.2f}' for wr in self.win_rates]}"

# Simple two-player game environment (Rock-Paper-Scissors)
class RockPaperScissors:
    def __init__(self):
        self.action_names = ['Rock', 'Paper', 'Scissors']
        self.num_actions = 3
    
    def play_game(self, agent1, agent2, num_rounds=10):
        """Play a game between two agents"""
        agent1_wins = 0
        agent2_wins = 0
        
        agent1_trajectory = {'states': [], 'actions': [], 'rewards': [], 'action_probs': []}
        agent2_trajectory = {'states': [], 'actions': [], 'rewards': [], 'action_probs': []}
        
        for round_num in range(num_rounds):
            # Simple state: round number normalized
            state = [round_num / num_rounds]
            
            # Get actions from both agents
            action1, probs1 = agent1.get_action(state)
            action2, probs2 = agent2.get_action(state)
            
            # Determine winner
            if action1 == action2:
                reward1, reward2 = 0, 0  # Tie
            elif (action1 == 0 and action2 == 2) or \\
                 (action1 == 1 and action2 == 0) or \\
                 (action1 == 2 and action2 == 1):
                reward1, reward2 = 1, -1  # Agent 1 wins
                agent1_wins += 1
            else:
                reward1, reward2 = -1, 1  # Agent 2 wins
                agent2_wins += 1
            
            # Store trajectories
            agent1_trajectory['states'].append(state)
            agent1_trajectory['actions'].append(action1)
            agent1_trajectory['rewards'].append(reward1)
            agent1_trajectory['action_probs'].append(probs1)
            
            agent2_trajectory['states'].append(state)
            agent2_trajectory['actions'].append(action2)
            agent2_trajectory['rewards'].append(reward2)
            agent2_trajectory['action_probs'].append(probs2)
        
        # Determine game winner
        if agent1_wins > agent2_wins:
            game_result = 1  # Agent 1 wins
        elif agent2_wins > agent1_wins:
            game_result = -1  # Agent 2 wins
        else:
            game_result = 0  # Tie
        
        return game_result, agent1_trajectory, agent2_trajectory

class SelfPlayTrainer:
    def __init__(self, state_size, action_size):
        self.current_agent = SelfPlayAgent(state_size, action_size)
        self.opponent_pool = OpponentPool(max_size=5)
        self.env = RockPaperScissors()
        
        # Training statistics
        self.training_stats = {
            'win_rates': [],
            'policy_losses': [],
            'value_losses': []
        }
    
    def train(self, num_iterations=1000, games_per_iteration=10):
        """Train agent using self-play"""
        
        for iteration in range(num_iterations):
            total_wins = 0
            total_games = 0
            policy_losses = []
            value_losses = []
            
            for game in range(games_per_iteration):
                # Select opponent
                if random.random() < 0.3 and len(self.opponent_pool.agents) > 0:
                    # Play against opponent from pool
                    opponent = self.opponent_pool.sample_opponent(self.current_agent.win_rate)
                else:
                    # Play against current agent (self-play)
                    opponent = copy.deepcopy(self.current_agent)
                
                # Play game
                result, agent_trajectory, opponent_trajectory = self.env.play_game(
                    self.current_agent, opponent, num_rounds=5
                )
                
                # Update statistics
                if result == 1:
                    total_wins += 1
                total_games += 1
                
                # Update current agent
                policy_loss, value_loss = self.current_agent.update(
                    agent_trajectory['states'],
                    agent_trajectory['actions'],
                    agent_trajectory['rewards'],
                    agent_trajectory['action_probs']
                )
                
                policy_losses.append(policy_loss)
                value_losses.append(value_loss)
            
            # Calculate win rate
            win_rate = total_wins / total_games if total_games > 0 else 0.0
            self.current_agent.win_rate = win_rate
            self.current_agent.games_played += total_games
            
            # Record statistics
            self.training_stats['win_rates'].append(win_rate)
            self.training_stats['policy_losses'].append(np.mean(policy_losses))
            self.training_stats['value_losses'].append(np.mean(value_losses))
            
            # Add current agent to opponent pool periodically
            if iteration % 50 == 0 and iteration > 0:
                self.opponent_pool.add_agent(self.current_agent, win_rate)
                print(f"Added agent to pool. {self.opponent_pool.get_stats()}")
            
            # Print progress
            if iteration % 100 == 0:
                avg_win_rate = np.mean(self.training_stats['win_rates'][-100:])
                avg_policy_loss = np.mean(self.training_stats['policy_losses'][-100:])
                print(f"Iteration {iteration}: Win Rate: {avg_win_rate:.3f}, Policy Loss: {avg_policy_loss:.4f}")
        
        print("Self-play training completed!")
        return self.training_stats

# Example usage
trainer = SelfPlayTrainer(state_size=1, action_size=3)
stats = trainer.train(num_iterations=1000, games_per_iteration=20)

print(f"Final win rate: {trainer.current_agent.win_rate:.3f}")
print(f"Total games played: {trainer.current_agent.games_played}")
print(f"Agent version: {trainer.current_agent.version}")
`,
        language: 'python',
        category: 'tutorial'
      }
    ],
    practice: {
      task: 'Extend the self-play system to include population-based training with diversity metrics and league-based competition.',
      hints: [
        'Implement behavioral distance metrics between agents',
        'Create a league system with promotion/relegation',
        'Add diversity bonuses to encourage varied strategies'
      ]
    }
  }
];